/**
 * InvoiceView - Clean Joist-style invoice view page
 * Shows invoice as a document with action buttons and payments panel
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import PdfViewerModal from '../components/shared/PdfViewerModal';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const fmt = (v) => Number(v || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '';
  const str = typeof d === 'string' ? d : d.toISOString();
  const parts = str.split('T')[0].split('-');
  const dt = new Date(+parts[0], +parts[1] - 1, +parts[2]);
  return dt.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtDateVerbose = (d) => {
  if (!d) return '';
  const str = typeof d === 'string' ? d : d.toISOString();
  const parts = str.split('T')[0].split('-');
  const dt = new Date(+parts[0], +parts[1] - 1, +parts[2]);
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase();
};

const PAYMENT_METHODS = ['Cash', 'Cheque', 'E-Transfer', 'Credit Card', 'Debit', 'Bank Transfer', 'Other'];

const InvoiceView = ({ invoice: initialInvoice, onBack, onEdit, currentUser }) => {
  const [invoice, setInvoice] = useState(initialInvoice || {});
  const [settings, setSettings] = useState(null);
  const [showPaymentsPanel, setShowPaymentsPanel] = useState(false);
  const [paymentView, setPaymentView] = useState('list'); // 'list' | 'record'
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    amountType: '$',
    paid_date: new Date().toISOString().split('T')[0],
    method: 'Cash',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendForm, setSendForm] = useState({ recipient_email: '', message: '', attach_pdf: false, cc_admin: false, email_type: 'invoice' });
  const [isSending, setIsSending] = useState(false);
  const [paymentMenuId, setPaymentMenuId] = useState(null);
  const [pdfView, setPdfView] = useState(null); // { type: 'invoice'|'receipt', id, title }
  const moreRef = useRef(null);

  const isAdmin = currentUser?.rawRole === 'admin';

  useEffect(() => {
    const loadData = async () => {
      try {
        const [settingsRes] = await Promise.all([
          axios.get(`${API}/sales/settings`)
        ]);
        setSettings(settingsRes.data);
      } catch (e) {
        console.error('Error loading settings:', e);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (!initialInvoice?.id) return;
    axios.get(`${API}/sales/invoices/${initialInvoice.id}?t=${Date.now()}`)
      .then(r => setInvoice(r.data))
      .catch(e => console.error('Error refreshing invoice:', e));
  }, [initialInvoice?.id]);

  // Close more menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const realPayments = (invoice.payments || []).filter(p => !String(p.method || '').startsWith('Schedule:'));
  const progressPct = invoice.total > 0 ? Math.min(100, ((invoice.amount_paid || 0) / invoice.total) * 100) : 0;
  const lineItems = (invoice.line_items || []);

  const openPaymentsPanel = () => {
    setShowPaymentsPanel(true);
    setPaymentView('list');
    setPaymentMenuId(null);
  };

  const openRecordPayment = () => {
    setPaymentForm({
      amount: '',
      amountType: '$',
      paid_date: new Date().toISOString().split('T')[0],
      method: 'Cash',
      notes: ''
    });
    setPaymentView('record');
  };

  const getPaymentAmount = () => {
    if (paymentForm.amountType === 'full') return invoice.balance_due || 0;
    if (paymentForm.amountType === '%') {
      const pct = parseFloat(paymentForm.amount) || 0;
      return (invoice.total || 0) * pct / 100;
    }
    return parseFloat(paymentForm.amount) || 0;
  };

  const handleRecordPayment = async () => {
    const amount = getPaymentAmount();
    if (!amount || amount <= 0) {
      alert('Please enter a valid amount.');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/sales/invoices/${invoice.id}/payments`, {
        amount,
        method: paymentForm.method,
        paid_date: paymentForm.paid_date,
        notes: paymentForm.notes
      });
      const updated = r.data?.invoice || r.data;
      if (updated?.id) setInvoice(updated);
      setPaymentView('list');
    } catch (e) {
      console.error('Error recording payment:', e);
      alert('Unable to record payment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePayment = async (paymentId, idx) => {
    if (!window.confirm('Delete this payment? This cannot be undone.')) return;
    setPaymentMenuId(null);
    try {
      const r = await axios.delete(`${API}/sales/invoices/${invoice.id}/payments/${encodeURIComponent(paymentId)}`, {
        params: paymentId ? undefined : { index: idx }
      });
      const updated = r.data?.invoice || r.data;
      if (updated?.id) setInvoice(updated);
    } catch (e) {
      console.error('Error deleting payment:', e);
      alert('Unable to delete payment.');
    }
  };

  const handleSendInvoice = async () => {
    if (!sendForm.recipient_email) {
      alert('Recipient email is required.');
      return;
    }
    setIsSending(true);
    try {
      await axios.post(`${API}/sales/invoices/${invoice.id}/send`, {
        ...sendForm,
        base_url: window.location.origin
      });
      alert('Invoice sent successfully.');
      setShowSendModal(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      alert(detail || 'Unable to send invoice. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const openSendModal = () => {
    const hasPaid = Number(invoice.amount_paid || 0) > 0;
    setSendForm({
      recipient_email: invoice.client_email || '',
      message: settings?.invoice_email_message || '',
      attach_pdf: false,
      cc_admin: false,
      email_type: hasPaid ? 'receipt' : 'invoice',
    });
    setShowSendModal(true);
  };

  const companyName = settings?.company_name || '';
  const companyCityLine = [settings?.company_city, settings?.company_province].filter(Boolean).join(', ');
  const hstNumber = settings?.hst_number || '';

  return (
    <>
    <div className="min-h-screen bg-gray-100" data-testid="invoice-view-page">
      {/* ── Top Action Bar ── */}
      <div className="bg-white border-b sticky top-0 z-20 shadow-sm">
        <div className="container mx-auto px-3 sm:px-6 py-2.5 flex items-center justify-between gap-2">
          {/* Left: Back + title */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={onBack}
              className="text-gray-500 hover:text-gray-700 font-medium text-sm flex-shrink-0 flex items-center gap-1"
              data-testid="invoice-view-back"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden sm:inline">Back</span>
            </button>
            <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate" data-testid="invoice-view-title">
              Invoice #{invoice.invoice_number}
            </h1>
            {invoice.status && (
              <span className={`hidden sm:inline-flex px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0 ${
                invoice.status === 'PAID' ? 'bg-green-100 text-green-700' :
                invoice.status === 'PARTIAL' ? 'bg-yellow-100 text-yellow-700' :
                invoice.status === 'ISSUED' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-600'
              }`} data-testid="invoice-view-status">
                {invoice.status}
              </span>
            )}
          </div>

          {/* Right: Action buttons */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <button
              onClick={onEdit}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-semibold text-xs sm:text-sm border border-gray-200 transition-colors"
              data-testid="invoice-view-edit-btn"
            >
              EDIT
            </button>
            <button
              onClick={openSendModal}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold text-xs sm:text-sm flex items-center gap-1.5 transition-colors"
              data-testid="invoice-view-email-btn"
            >
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
              </svg>
              <span className="hidden sm:inline">EMAIL</span>
            </button>
            <button
              onClick={openPaymentsPanel}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-semibold text-xs sm:text-sm flex items-center gap-1.5 border border-gray-200 transition-colors"
              data-testid="invoice-view-payments-btn"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <span>PAYMENTS</span>
            </button>
            <div className="relative" ref={moreRef}>
              <button
                onClick={() => setShowMoreMenu(p => !p)}
                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-semibold text-xs sm:text-sm flex items-center gap-1 border border-gray-200 transition-colors"
                data-testid="invoice-view-more-btn"
              >
                MORE
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showMoreMenu && (
                <div className="absolute right-0 mt-1.5 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-30" data-testid="invoice-view-more-menu">
                  <button
                    onClick={() => { setShowMoreMenu(false); setPdfView({ type: 'invoice', id: invoice.id, title: `Invoice #${invoice.invoice_number}` }); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg flex items-center gap-2"
                    data-testid="invoice-view-more-pdf-invoice"
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    View as Invoice
                  </button>
                  <button
                    onClick={() => { setShowMoreMenu(false); setPdfView({ type: 'receipt', id: invoice.id, title: `Receipt #${invoice.invoice_number}` }); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    data-testid="invoice-view-more-pdf-receipt"
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    View as Receipt
                  </button>
                  {invoice.public_token && (
                    <button
                      onClick={() => { setShowMoreMenu(false); window.open(`${window.location.origin}/invoice/${invoice.public_token}?v=${Date.now()}`, '_blank'); }}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      data-testid="invoice-view-more-public-link"
                    >
                      Public Link
                    </button>
                  )}
                  <div className="border-t border-gray-100" />
                  <button
                    onClick={onEdit}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg"
                    data-testid="invoice-view-more-edit"
                  >
                    Edit Invoice
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Invoice Document Body ── */}
      <div className="container mx-auto px-3 sm:px-6 py-6 max-w-4xl">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-10" data-testid="invoice-view-document">

          {/* Document Header */}
          <div className="flex flex-col sm:flex-row sm:justify-between gap-6 mb-8">
            {/* Left: Logo + Company */}
            <div className="flex flex-col gap-3">
              <img
                src={`${API}/sales/logo`}
                alt={companyName}
                className="h-16 w-auto object-contain object-left"
                onError={(e) => { e.target.style.display = 'none'; }}
                data-testid="invoice-view-company-logo"
              />
              <div className="text-sm text-gray-700 leading-relaxed" data-testid="invoice-view-company-info">
                <div className="font-semibold text-gray-900">{companyName}</div>
                <div>{settings?.company_address}</div>
                <div>{companyCityLine}{settings?.company_postal_code ? ` ${settings.company_postal_code}` : ''}</div>
                <div>Phone: {settings?.company_phone}</div>
                <div>Email: {settings?.company_email}</div>
                <div>Web: {settings?.company_website}</div>
              </div>
            </div>
            {/* Right: Bill To + Meta */}
            <div className="sm:text-right flex flex-col gap-4">
              <div data-testid="invoice-view-bill-to">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Bill To</div>
                <div className="text-sm text-gray-800 leading-relaxed">
                  <div className="font-semibold text-gray-900">{invoice.client_name}</div>
                  <div>{invoice.client_address}</div>
                  <div>{[invoice.client_city, invoice.client_province].filter(Boolean).join(', ')}{invoice.client_postal_code ? ` ${invoice.client_postal_code}` : ''}</div>
                  {invoice.client_phone && <div>{invoice.client_phone}</div>}
                </div>
              </div>
              <div className="text-sm text-gray-700 space-y-1" data-testid="invoice-view-meta">
                {invoice.due_date && (
                  <div className="flex sm:justify-end gap-4">
                    <span className="text-gray-500">Payment terms</span>
                    <span className="font-medium text-gray-900">Due upon receipt</span>
                  </div>
                )}
                <div className="flex sm:justify-end gap-4">
                  <span className="text-gray-500">Invoice #</span>
                  <span className="font-medium text-gray-900">{invoice.invoice_number}</span>
                </div>
                <div className="flex sm:justify-end gap-4">
                  <span className="text-gray-500">Date</span>
                  <span className="font-medium text-gray-900">{fmtDate(invoice.date)}</span>
                </div>
                {hstNumber && (
                  <div className="flex sm:justify-end gap-4">
                    <span className="text-gray-500">Business / Tax #</span>
                    <span className="font-medium text-gray-900">{hstNumber}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Line Items Table */}
          <div className="mb-6" data-testid="invoice-view-line-items">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-900">
                  <th className="text-left pb-2 font-semibold text-gray-900">Description</th>
                  <th className="text-right pb-2 font-semibold text-gray-900 w-28">Total</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, idx) => {
                  const lines = (item.description || '').split('\n').filter(Boolean);
                  const title = lines[0] || '';
                  const body = lines.slice(1);
                  const itemTotal = (Number(item.quantity || 0) * Number(item.unit_cost || 0)) * (1 + (Number(item.markup_percentage || 0) / 100));
                  return (
                    <tr key={item.id || idx} className="border-b border-gray-100" data-testid={`invoice-view-item-${idx}`}>
                      <td className="py-3 pr-4">
                        {title && <div className="font-semibold text-gray-900">{title}</div>}
                        {body.map((l, i) => <div key={i} className="text-gray-500 text-xs mt-0.5">{l}</div>)}
                        {item.is_change_order && (
                          <span className="inline-flex items-center mt-1 px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-700">Change Order</span>
                        )}
                      </td>
                      <td className="py-3 text-right font-medium text-gray-900">${fmt(itemTotal)}</td>
                    </tr>
                  );
                })}
                {lineItems.length === 0 && (
                  <tr><td colSpan="2" className="py-4 text-center text-gray-400 text-sm">No line items</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-6" data-testid="invoice-view-totals">
            <div className="w-full sm:w-72 space-y-1.5 text-sm">
              <div className="flex justify-between text-gray-700">
                <span>Subtotal</span>
                <span className="font-medium">${fmt(invoice.subtotal)}</span>
              </div>
              {Number(invoice.markup_amount) > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Markup</span>
                  <span className="font-medium">${fmt(invoice.markup_amount)}</span>
                </div>
              )}
              {Number(invoice.discount_amount) > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Discount</span>
                  <span className="font-medium">-${fmt(invoice.discount_amount)}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-700">
                <span>Tax ({invoice.tax_rate || 0}%)</span>
                <span className="font-medium">${fmt(invoice.tax_amount)}</span>
              </div>
              {invoice.cc_fee_enabled && Number(invoice.cc_fee_amount) > 0 && (
                <div className="flex justify-between text-gray-700" data-testid="invoice-view-cc-fee">
                  <span>CC Processing Fee{invoice.cc_fee_type !== 'flat' ? ` (${invoice.cc_fee_value}%)` : ''}</span>
                  <span className="font-medium">+${fmt(invoice.cc_fee_amount)}</span>
                </div>
              )}
              {/* Payment Schedule — with per-milestone progress */}
              {(invoice.payment_schedule || []).filter(s => !String(s.method || '').startsWith('Schedule:')).length > 0 && (() => {
                const schedItems = (invoice.payment_schedule || []).filter(s => !String(s.method || '').startsWith('Schedule:'));
                const schedType = invoice.payment_schedule_type || 'percent';
                const total = invoice.total || 0;
                // Allocate actual payments sequentially (FIFO) across milestones (including CC fee tracking)
                let remaining = invoice.amount_paid || 0;
                const schedPayments = (invoice.payments || [])
                  .filter(p => !String(p.method || '').startsWith('Schedule:'))
                  .map(p => ({
                    left: parseFloat(p.amount || 0),
                    ccFee: p.cc_fee_enabled ? (parseFloat(p.cc_fee_amount || 0)) : 0,
                    total: Math.max(parseFloat(p.amount || 0), 0.01)
                  }));
                let pmtPtr = 0;
                const allocs = schedItems.map(s => {
                  const due = schedType === 'percent' ? total * (Number(s.amount || 0) / 100) : Number(s.amount || 0);
                  const paid = Math.min(due, remaining);
                  remaining = Math.max(0, remaining - paid);
                  let ccFeeForEntry = 0;
                  let need = paid;
                  let i = pmtPtr;
                  while (need > 0.001 && i < schedPayments.length) {
                    const sp = schedPayments[i];
                    const use = Math.min(need, sp.left);
                    ccFeeForEntry += (use / sp.total) * sp.ccFee;
                    need -= use;
                    sp.left -= use;
                    if (sp.left <= 0.001) i++;
                  }
                  pmtPtr = i;
                  return { ...s, due, paid, balance: due - paid, ccFeeFromPayment: Math.round(ccFeeForEntry * 100) / 100 };
                });
                return (
                  <div className="border-t border-gray-100 pt-2 mt-2" data-testid="invoice-view-schedule">
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Payment Schedule</div>
                    <div className="space-y-2">
                      {allocs.map((a, i) => (
                        <div key={a.id || i} data-testid={`invoice-view-schedule-item-${i}`}>
                          <div className="flex justify-between text-xs text-gray-700">
                            <span className="truncate mr-2 font-medium">{a.name || `Payment ${i + 1}`}</span>
                            <span className="flex-shrink-0 font-medium">
                              {schedType === 'percent' ? `${Number(a.amount || 0)}% · ` : ''}${fmt(a.due)}
                            </span>
                          </div>
                          {a.due > 0 && (
                            <div className="flex flex-col items-end mt-0.5 gap-0.5">
                              {a.balance <= 0 ? (
                                <>
                                  <span className="text-xs text-emerald-600 font-semibold" data-testid={`invoice-view-schedule-status-${i}`}>✓ Paid in Full</span>
                                  {a.ccFeeFromPayment > 0 && (
                                    <span className="text-xs text-blue-600" data-testid={`invoice-view-schedule-cc-fee-${i}`}>CC fee: +${fmt(a.ccFeeFromPayment)}</span>
                                  )}
                                </>
                              ) : a.paid > 0 ? (
                                <span className="text-xs text-amber-600 font-medium" data-testid={`invoice-view-schedule-status-${i}`}>
                                  Paid ${fmt(a.paid)} · <strong>${fmt(a.balance)} remaining</strong>
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400" data-testid={`invoice-view-schedule-status-${i}`}>Due</span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <div className="flex justify-between border-t border-gray-900 pt-2 text-base font-bold text-gray-900">
                <span>Total (CAD)</span>
                <span data-testid="invoice-view-total">${fmt(invoice.total)}</span>
              </div>
              {Number(invoice.amount_paid) > 0 && (
                <div className="flex justify-between text-emerald-700 font-semibold">
                  <span>Amount Paid</span>
                  <span data-testid="invoice-view-amount-paid">${fmt(invoice.amount_paid)}</span>
                </div>
              )}
              {Number(invoice.total_cc_fees_paid) > 0 && (
                <div className="flex justify-between text-blue-600" data-testid="invoice-view-cc-fees">
                  <span>CC Fees</span>
                  <span>+${fmt(invoice.total_cc_fees_paid)}</span>
                </div>
              )}
              {Number(invoice.total_cc_fees_paid) > 0 && (
                <div className="flex justify-between text-emerald-700 font-semibold" data-testid="invoice-view-total-paid">
                  <span>Total Paid</span>
                  <span>${fmt(Number(invoice.amount_paid || 0) + Number(invoice.total_cc_fees_paid || 0))}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-gray-200 pt-2 text-base font-bold">
                <span className={invoice.balance_due <= 0 ? 'text-emerald-700' : 'text-gray-900'}>
                  {invoice.balance_due <= 0 ? 'Paid in Full' : 'Remaining Amount'}
                </span>
                <span className={invoice.balance_due <= 0 ? 'text-emerald-700' : 'text-gray-900'} data-testid="invoice-view-balance">
                  ${fmt(invoice.balance_due)}
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="border-t border-gray-100 pt-4 text-sm text-gray-700" data-testid="invoice-view-notes">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes</div>
              <div className="whitespace-pre-wrap">{invoice.notes}</div>
            </div>
          )}
        </div>

        {/* Payment History (below document) */}
        {realPayments.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-4 p-6" data-testid="invoice-view-payment-history">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Payment History</h3>
              {isAdmin && (
                <button
                  onClick={() => { openPaymentsPanel(); }}
                  className="text-emerald-600 hover:text-emerald-700 text-sm font-semibold"
                  data-testid="invoice-view-record-payment-link"
                >
                  + Record Payment
                </button>
              )}
            </div>
            <div className="divide-y divide-gray-100">
              {realPayments.map((p, i) => (
                <div key={p.id || i} className="py-2.5 flex items-center justify-between" data-testid={`invoice-view-payment-history-row-${i}`}>
                  <div>
                    <span className="font-semibold text-gray-900">${fmt(p.amount)}</span>
                    <span className="text-xs text-gray-500 ml-2">{fmtDate(p.paid_date)}{p.method ? ` · ${p.method}` : ''}</span>
                    {p.notes && <div className="text-xs text-gray-400">{p.notes}</div>}
                    {p.cc_fee_enabled && Number(p.cc_fee_amount) > 0 && (
                      <div className="text-xs text-blue-600 mt-0.5" data-testid={`invoice-view-payment-cc-fee-${i}`}>
                        CC fee: +${fmt(p.cc_fee_amount)}{p.cc_fee_type !== 'flat' ? ` (${p.cc_fee_value}%)` : ''} · Total charged: ${fmt(parseFloat(p.amount || 0) + parseFloat(p.cc_fee_amount || 0))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Payments Panel (Modal) ── */}
      {showPaymentsPanel && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-12 sm:items-center sm:pt-0 p-4"
          onClick={() => setShowPaymentsPanel(false)}
          data-testid="invoice-payments-panel-overlay"
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="invoice-payments-panel"
          >
            {paymentView === 'list' ? (
              /* ── Payments List View ── */
              <>
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <h2 className="text-base font-bold text-gray-900">Payments</h2>
                  {isAdmin && (
                    <button
                      onClick={openRecordPayment}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide transition-colors"
                      data-testid="invoice-payments-record-btn"
                    >
                      Record Payment
                    </button>
                  )}
                </div>

                {/* Progress Bar */}
                <div className="px-5 py-3" data-testid="invoice-payments-progress">
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="font-bold text-gray-900">PAID</span>
                    <span className="font-bold text-gray-700">DUE ${fmt(invoice.balance_due)}</span>
                  </div>
                  <div className="h-8 rounded-md overflow-hidden bg-gray-300 relative">
                    <div
                      className="absolute inset-y-0 left-0 bg-emerald-600 flex items-center px-3 text-white text-xs font-bold transition-all duration-500"
                      style={{ width: `${Math.max(progressPct, 1)}%` }}
                      data-testid="invoice-payments-progress-bar"
                    />
                    <div className="absolute inset-0 flex items-center px-3 text-xs font-bold text-white pointer-events-none">
                      CAD ${fmt(invoice.amount_paid)} of ${fmt(invoice.total)}
                    </div>
                  </div>
                </div>

                {/* Payment List */}
                <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto" data-testid="invoice-payments-list">
                  {realPayments.length === 0 ? (
                    <div className="px-5 py-6 text-sm text-gray-400 text-center">No payments recorded yet.</div>
                  ) : (
                    realPayments.map((p, i) => (
                      <div key={p.id || i} className="px-5 py-3 flex items-center justify-between" data-testid={`invoice-payment-item-${i}`}>
                        <div>
                          <div className="text-base font-bold text-gray-900">${fmt(p.amount)}</div>
                          <div className="text-xs text-gray-500 mt-0.5 uppercase tracking-wide">
                            {fmtDateVerbose(p.paid_date)}{p.method ? ` / ${p.method.toUpperCase()}` : ''}
                          </div>
                          {p.notes && <div className="text-xs text-gray-400 mt-0.5">{p.notes}</div>}
                        </div>
                        {isAdmin && (
                          <div className="relative">
                            <button
                              onClick={() => setPaymentMenuId(paymentMenuId === (p.id || i) ? null : (p.id || i))}
                              className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600"
                              data-testid={`invoice-payment-menu-btn-${i}`}
                            >
                              ⋯
                            </button>
                            {paymentMenuId === (p.id || i) && (
                              <div className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                                <button
                                  onClick={() => handleDeletePayment(p.id, i)}
                                  className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                                  data-testid={`invoice-payment-delete-${i}`}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>

                <div className="border-t border-gray-100 px-5 py-3 flex justify-end">
                  <button
                    onClick={() => setShowPaymentsPanel(false)}
                    className="text-sm font-bold text-gray-500 hover:text-gray-700 uppercase tracking-wide"
                    data-testid="invoice-payments-close-btn"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              /* ── Record Payment View ── */
              <>
                <div className="px-5 py-4 flex items-center gap-2 border-b border-gray-100">
                  <button
                    onClick={() => setPaymentView('list')}
                    className="text-gray-700 flex items-center gap-1 font-bold"
                    data-testid="invoice-record-payment-back"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
                    </svg>
                    Record Payment
                  </button>
                </div>

                {/* Progress Bar */}
                <div className="px-5 py-3" data-testid="invoice-record-payment-progress">
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="font-bold text-gray-900">PAID</span>
                    <span className="font-bold text-gray-700">DUE ${fmt(invoice.balance_due)}</span>
                  </div>
                  <div className="h-8 rounded-md overflow-hidden bg-gray-300 relative">
                    <div
                      className="absolute inset-y-0 left-0 bg-emerald-600 flex items-center px-3 text-white text-xs font-bold transition-all duration-500"
                      style={{ width: `${Math.max(progressPct, 1)}%` }}
                    />
                    <div className="absolute inset-0 flex items-center px-3 text-xs font-bold text-white pointer-events-none">
                      CAD ${fmt(invoice.amount_paid)} of ${fmt(invoice.total)}
                    </div>
                  </div>
                </div>

                <div className="px-5 pb-5 space-y-4">
                  {/* Amount */}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Payment</label>
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        {paymentForm.amountType === '$' && (
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                        )}
                        <input
                          type="number"
                          value={paymentForm.amountType === 'full' ? (invoice.balance_due || 0) : paymentForm.amount}
                          onChange={(e) => setPaymentForm(p => ({ ...p, amount: e.target.value }))}
                          disabled={paymentForm.amountType === 'full'}
                          className={`w-full border border-gray-300 rounded-lg py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${paymentForm.amountType === '$' ? 'pl-7 pr-3' : 'px-3'} ${paymentForm.amountType === 'full' ? 'bg-gray-50 text-gray-500' : ''}`}
                          placeholder="0.00"
                          step="0.01"
                          data-testid="invoice-record-amount-input"
                        />
                      </div>
                      <div className="flex border border-gray-300 rounded-lg overflow-hidden text-sm font-semibold">
                        {['$', '%', 'Full'].map(t => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setPaymentForm(p => ({ ...p, amountType: t === 'Full' ? 'full' : t, amount: '' }))}
                            className={`px-3 py-2 transition-colors ${
                              (paymentForm.amountType === 'full' && t === 'Full') || paymentForm.amountType === t
                                ? 'bg-emerald-600 text-white'
                                : 'bg-white text-gray-600 hover:bg-gray-50'
                            }`}
                            data-testid={`invoice-record-amount-type-${t.toLowerCase()}`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Date */}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Payment Date</label>
                    <input
                      type="date"
                      value={paymentForm.paid_date}
                      onChange={(e) => setPaymentForm(p => ({ ...p, paid_date: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      data-testid="invoice-record-date-input"
                    />
                  </div>

                  {/* Method */}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Payment Method</label>
                    <div className="flex flex-wrap gap-2" data-testid="invoice-record-method-buttons">
                      {PAYMENT_METHODS.map(m => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setPaymentForm(p => ({ ...p, method: m }))}
                          className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${
                            paymentForm.method === m
                              ? 'bg-gray-900 border-gray-900 text-white'
                              : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                          }`}
                          data-testid={`invoice-record-method-${m.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Notes</label>
                    <input
                      type="text"
                      value={paymentForm.notes}
                      onChange={(e) => setPaymentForm(p => ({ ...p, notes: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="Optional"
                      data-testid="invoice-record-notes-input"
                    />
                  </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-100 px-5 py-4 flex items-center justify-end gap-4">
                  <button
                    onClick={() => setPaymentView('list')}
                    className="text-sm font-bold text-gray-500 hover:text-gray-700 uppercase tracking-wide"
                    data-testid="invoice-record-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRecordPayment}
                    disabled={saving}
                    className="text-sm font-bold text-emerald-600 hover:text-emerald-700 uppercase tracking-wide disabled:text-gray-400"
                    data-testid="invoice-record-add-btn"
                  >
                    {saving ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Send Modal ── */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="invoice-view-send-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl flex flex-col" style={{maxHeight: '90vh'}}>
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 flex-shrink-0">
              <h3 className="text-base font-bold text-gray-900">
                {sendForm.email_type === 'receipt' ? 'Send Receipt' : 'Send Invoice'}
              </h3>
              <button onClick={() => setShowSendModal(false)} className="text-gray-400 hover:text-gray-600 p-1" data-testid="invoice-view-send-close">✕</button>
            </div>
            <div className="p-6 flex flex-col flex-1 overflow-y-auto gap-4" style={{minHeight: 0}}>
              {/* Invoice / Receipt toggle */}
              <div className="flex rounded-lg border border-gray-200 overflow-hidden self-start flex-shrink-0" data-testid="invoice-view-send-type-toggle">
                <button
                  onClick={() => setSendForm(p => ({ ...p, email_type: 'invoice' }))}
                  className={`px-5 py-1.5 text-sm font-medium transition-colors ${sendForm.email_type === 'invoice' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  data-testid="invoice-view-send-type-invoice"
                >
                  Invoice
                </button>
                <button
                  onClick={() => setSendForm(p => ({ ...p, email_type: 'receipt' }))}
                  className={`px-5 py-1.5 text-sm font-medium transition-colors ${sendForm.email_type === 'receipt' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  data-testid="invoice-view-send-type-receipt"
                >
                  Receipt
                </button>
              </div>
              <div className="flex-shrink-0">
                <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email</label>
                <input
                  type="email"
                  value={sendForm.recipient_email}
                  onChange={(e) => setSendForm(p => ({ ...p, recipient_email: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500"
                  data-testid="invoice-view-send-email"
                />
              </div>
              <div className="flex flex-col flex-1" style={{minHeight: '200px'}}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={sendForm.message}
                  onChange={(e) => setSendForm(p => ({ ...p, message: e.target.value }))}
                  className="w-full flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 resize-none"
                  style={{minHeight: '300px'}}
                  data-testid="invoice-view-send-message"
                />
              </div>
              <div className="flex items-center gap-6 flex-shrink-0">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendForm.attach_pdf}
                    onChange={(e) => setSendForm(p => ({ ...p, attach_pdf: e.target.checked }))}
                    className="rounded"
                    data-testid="invoice-view-send-attach-pdf"
                  />
                  Attach PDF
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendForm.cc_admin}
                    onChange={(e) => setSendForm(p => ({ ...p, cc_admin: e.target.checked }))}
                    className="rounded"
                    data-testid="invoice-view-send-cc-admin"
                  />
                  CC myself
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4 flex-shrink-0">
              <button onClick={() => setShowSendModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700" data-testid="invoice-view-send-cancel">Cancel</button>
              <button
                onClick={handleSendInvoice}
                disabled={isSending}
                className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold disabled:bg-gray-400"
                data-testid="invoice-view-send-confirm"
              >
                {isSending ? 'Sending...' : sendForm.email_type === 'receipt' ? 'Send Receipt' : 'Send Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    {pdfView && (
      <PdfViewerModal
        formType={pdfView.type}
        formId={pdfView.id}
        title={pdfView.title}
        onClose={() => setPdfView(null)}
      />
    )}
  </>
  );
};

export default InvoiceView;
